import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import api from "../lib/api";
import { isNoldDomain } from "../lib/domain";
import { useZone } from "../context/ZoneContext";

export const AssistantWidget = () => {
  const { zone } = useZone();
  const BRAND_NAME = () => (zone === "europe" || isNoldDomain() ? "Nold Go" : "BentSkin Go");
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && msgs.length === 0) {
      api.get("/assistant/history").then((r) => {
        const h = r.data.messages || [];
        setMsgs(h.length ? h : [{ role: "assistant", text: `Bonjour 👋 Je suis l'assistant ${BRAND_NAME()}. Comment puis-je vous aider ? (tarifs, activation, statut de votre course…)` }]);
      }).catch(() => setMsgs([{ role: "assistant", text: "Bonjour 👋 Comment puis-je vous aider ?" }]));
    }
  }, [open, msgs.length]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async () => {
    const t = input.trim();
    if (!t || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: t }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: t });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", text: "Désolé, je suis momentanément indisponible. Réessayez dans un instant." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button data-testid="assistant-open" onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full bg-[#F26419] hover:bg-[#d94f0f] text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105" aria-label="Assistant">
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
      {open && (
        <div data-testid="assistant-panel" className="fixed bottom-5 right-5 z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-3xl shadow-2xl border border-[#E2DFD8] flex flex-col overflow-hidden">
          <div className="bg-gradient-to-br from-[#F26419] to-[#d94f0f] text-white p-4 flex items-center justify-between">
            <div><div className="font-black">Assistant {BRAND_NAME()}</div><div className="text-xs text-white/90">IA · réponse instantanée</div></div>
            <button data-testid="assistant-close" onClick={() => setOpen(false)} className="p-1 rounded-lg hover:bg-white/20"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FDF8F0]">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${m.role === "user" ? "bg-[#F26419] text-white rounded-br-sm" : "bg-white border border-[#E2DFD8] text-[#1A1A1A] rounded-bl-sm"}`}>{m.text}</div>
              </div>
            ))}
            {busy && <div className="text-xs text-[#75807A]">L'assistant écrit…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-[#E2DFD8] flex items-center gap-2">
            <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Écrivez votre question…" className="flex-1 h-11 px-3 rounded-xl border border-[#E2DFD8] text-sm outline-none focus:border-[#F26419]" />
            <button data-testid="assistant-send" onClick={send} disabled={busy} className="h-11 w-11 rounded-xl bg-[#F26419] hover:bg-[#d94f0f] text-white flex items-center justify-center disabled:opacity-50"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </>
  );
};
